import { IAppNavigation } from '@/@types/AppNavigation';
import { VariantProps } from 'class-variance-authority';
import { useFlagsmith } from 'flagsmith/react';
import {
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardEdit,
  FileStack,
  FileText,
  Home,
  Menu,
  Receipt,
  Store,
  Users2,
} from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import DealerWhiteLogo from '@/assets/icons/concessionarias-white.svg';
import DealerLogo from '@/assets/icons/dealers.svg?react';
import SystemTinyLogoImg from '@/assets/icons/logo.svg';
import QToolLogo from '@/assets/icons/qtool.svg';
import { Tooltip } from '@/common/components/Tooltip';
import { buildInitialPeriod } from '@/common/hooks/useCalculatePeriods';
import { links } from '@/common/utils/links';

import { Config } from './components/Config';
import { NavItem } from './components/NavItem';
import * as style from './styles';

interface ISidebarStore {
  collapsed: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
}

export const useCollapseSidebar = create<ISidebarStore>()(
  persist(
    set => ({
      collapsed: false,
      toggleSidebar: () => set(state => ({ collapsed: !state.collapsed })),
      openSidebar: () => set({ collapsed: false }),
      closeSidebar: () => set({ collapsed: true }),
    }),
    { name: '@QTool:SidebarToggle' },
  ),
);

type ContainerProps = VariantProps<typeof style.container>;

export function Sidebar() {
  const [containerBottom, setContainerBottom] =
    useState<ContainerProps['containerBottom']>('bottom-auto');
  const [hideMenu, setHideMenu] = useState(true);

  const { hasFeature } = useFlagsmith();
  const { collapsed, toggleSidebar } = useCollapseSidebar();
  const { t } = useTranslation('sidebar/menus');

  const handleToggle = () => {
    toggleSidebar();
  };

  const handleToggleContainerStyle = () => {
    setContainerBottom(state =>
      state === 'bottom-auto' ? 'bottom-0' : 'bottom-auto',
    );
  };

  useLayoutEffect(() => {
    window.HSStaticMethods.autoInit();
  }, [hideMenu]);

  const MENUS: IAppNavigation[] = [
    {
      key: 'panel',
      title: t('sidebar/menus:panel'),
      link: links.panel,
      activePaths: [links.panel],
      flag: 'sidebar_panel_menu',
      icon: Home,
    },
    {
      key: 'quotes',
      title: t('sidebar/menus:quotes'),
      link: links.quotes.list,
      activePaths: [
        links.quotes.list,
        links.quotes.new,
        links.quotes.steps,
        links.quotes.newProduct,
        links.quotes.contractSteps,
        links.quotes.newContract,
        links.quotes.cart(),
      ],
      flag: 'sidebar_quotes_menu',
      icon: Receipt,
    },
    {
      key: 'contract',
      title: t('sidebar/menus:contract'),
      link: links.contract,
      flag: 'sidebar_minutes_menu',
      icon: ClipboardEdit,
    },
    // TODO: REATIVAR ESTE MENU QUANDO FOR INSERIR O PRECIFICADOR
    // {
    //   key: 'pricer',
    //   title: t('sidebar/menus:pricer'),
    //   link: links.pricer,
    //   flag: 'sidebar_pricer_menu',
    //   icon: Calculator,
    // },
    {
      key: 'clients',
      title: t('sidebar/menus:clients'),
      icon: Users2,
      link: links.clients,
      flag: 'sidebar_clients_menu',
    },
    {
      key: 'dealers',
      title: t('sidebar/menus:dealers'),
      icon: () => <DealerLogo className="h-[18px] w-[18px]" />,
      activeIcon: () => (
        <img src={DealerWhiteLogo} alt={t('sidebar/menus:dealers')} />
      ),
      link: links.dealers,
      flag: 'sidebar_dealer_menu',
    },
    {
      key: 'store',
      title: t('sidebar/menus:stores'),
      link: links.stores,
      icon: Store,
      flag: 'sidebar_store_menu',
    },
    {
      key: 'reports',
      title: t('sidebar/menus:reports'),
      icon: FileText,
      link: links.reports,
      flag: 'sidebar_reports_menu',
    },
    {
      key: 'invoices',
      title: t('sidebar/menus:invoices'),
      icon: FileStack,
      link: links.invoices.invoices,
      flag: 'sidebar_invoice_menu',
    },
    {
      key: 'invoicing',
      title: t('sidebar/menus:invoicing'),
      icon: CircleDollarSign,
      link: links.invoicing.invoicing + `?period=${buildInitialPeriod().value}`,
      flag: 'sidebar_invoicing_menu',
      activePaths: [
        links.invoicing.invoicing,
        links.invoicing.batchInvoices(),
        links.invoicing.openInvoices,
        links.invoicing.batchCompliance(),
      ],
    },
  ];

  return (
    <div
      className={style.container({ containerBottom })}
      data-collapsed={collapsed}
    >
      <button
        type="button"
        className={style.collapseButton()}
        onClick={handleToggle}
      >
        <ChevronLeft size={14} className="group-data-[collapsed=true]:hidden" />
        <ChevronRight
          size={14}
          className="hidden group-data-[collapsed=true]:block"
        />
      </button>

      <section className={style.header()}>
        <img
          src={QToolLogo}
          alt="Iveco"
          className={`${style.logo()} group-data-[collapsed=true]:hidden`}
        />
        <img
          src={SystemTinyLogoImg}
          alt="Iveco"
          className={`${style.logo()} hidden group-data-[collapsed=true]:block`}
        />

        <button
          type="button"
          className="hs-collapse-toggle rounded-sm bg-brand-pure p-1 text-white md:hidden"
          data-hs-collapse="#hs-basic-collapse-heading"
          onClick={() => {
            handleToggleContainerStyle();
            setHideMenu(state => !state);
          }}
        >
          <Menu size={20} />
        </button>
      </section>

      <div
        id="hs-basic-collapse-heading"
        onClick={() => {
          setHideMenu(true);
          handleToggleContainerStyle();
        }}
        className={`${style.content()} ${hideMenu ? 'hidden' : ''}`}
      >
        <nav className={style.menu()}>
          {MENUS.map(menu => {
            if (menu.flag && !hasFeature(menu.flag)) return null;
            return (
              <Tooltip
                key={menu.key}
                message={menu.title}
                inactive={!collapsed}
              >
                <button
                  type="button"
                  data-hs-collapse="#hs-basic-collapse-heading"
                >
                  <NavItem data={menu} />
                </button>
              </Tooltip>
            );
          })}
        </nav>
        <Config />
      </div>
    </div>
  );
}
